import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, MessageCircle } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { waLink, WHATSAPP_BUSINESS } from "@/lib/api";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Line = ({ children, i, className }) => (
  <span className={`block overflow-hidden ${className || ""}`}>
    <motion.span
      custom={i}
      initial="hidden"
      animate="visible"
      variants={lineVariants}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onEnquire }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "20%"]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid={HERO.section}
      style={{ position: "relative" }}
      className="pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-10 max-w-7xl mx-auto overflow-hidden"
    >
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="text-xs md:text-sm uppercase tracking-[0.3em] font-bold text-[#D97736] mb-6"
          >
            KCI Registered · Govt Certified Breeder
          </motion.p>

          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] text-[#0F2520]">
            <Line i={0}>Raijada</Line>
            <Line i={1} className="text-[#D97736] italic">Dog Kennel</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="mt-8 text-base md:text-lg font-light leading-relaxed text-[#0F2520]/80 max-w-lg"
          >
            Authentic Golden Retrievers, raised with lifetime medical support from a qualified
            BVSc veterinarian — plus a lifetime of vaccination &amp; deworming reminders. Papers guaranteed.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              data-testid={HERO.ctaEnquire}
              onClick={onEnquire}
              className="rounded-full bg-[#D97736] text-white px-8 py-4 font-semibold tracking-wide hover:scale-105 active:scale-95 transition-transform duration-300 shadow-lg shadow-[#D97736]/30"
            >
              Enquire Now
            </button>
            <a
              data-testid={HERO.ctaWhatsapp}
              href={waLink(WHATSAPP_BUSINESS)}
              target="_blank"
              rel="noreferrer"
              className="rounded-full border-2 border-[#0F2520] text-[#0F2520] px-8 py-4 font-semibold tracking-wide hover:bg-[#0F2520] hover:text-white transition-colors duration-300 flex items-center gap-2"
            >
              <MessageCircle className="w-5 h-5" /> Chat on WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-10 flex items-center gap-2 text-[#0F2520]/70 text-sm"
          >
            <ShieldCheck className="w-5 h-5 text-[#0F2520]" />
            Lifetime vet support · Vaccination reminders · Trusted by 100+ families
          </motion.div>
        </div>

        <div className="md:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[3rem] overflow-hidden aspect-[3/4] shadow-2xl"
          >
            <motion.img
              style={{ y }}
              src="https://images.pexels.com/photos/12475794/pexels-photo-12475794.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=800"
              alt="Authentic Golden Retriever puppy"
              className="w-full h-[130%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2520]/40 via-transparent to-transparent" />
          </motion.div>
          <div className="absolute -bottom-6 -left-6 bg-[#F4D06F] text-[#0F2520] rounded-full px-6 py-3 font-display italic text-sm shadow-xl hidden md:block">
            Golden Retriever litter available now
          </div>
        </div>
      </div>
    </section>
  );
};
